/**
 * Media types, the variant ladder, and file validation rules.
 *
 * Everything here is shared by the API (which validates upload requests), the
 * worker (which produces variants) and the clients (which pick which variant to
 * request). Keeping one definition means a client can never ask for a variant
 * the worker does not produce.
 */

export const FILE_KINDS = ["photo", "video"] as const;
export type FileKind = (typeof FILE_KINDS)[number];

export const VARIANTS = [
  "thumb",
  "preview",
  "preview_hq",
  "poster",
  "video_720",
  "video_1080",
  "original",
] as const;
export type Variant = (typeof VARIANTS)[number];

export const IMAGE_FORMATS = ["avif", "webp", "jpeg"] as const;
export type ImageFormat = (typeof IMAGE_FORMATS)[number];

/* ─────────────────────────── Accepted input types ────────────────────────── */

interface MediaTypeSpec {
  kind: FileKind;
  extensions: readonly string[];
  /** Leading bytes that must be present. `null` at an index = wildcard. */
  magic: readonly (readonly (number | null)[])[];
}

export const ACCEPTED_MEDIA_TYPES: Record<string, MediaTypeSpec> = {
  "image/jpeg": {
    kind: "photo",
    extensions: [".jpg", ".jpeg"],
    magic: [[0xff, 0xd8, 0xff]],
  },
  "image/png": {
    kind: "photo",
    extensions: [".png"],
    magic: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  },
  "image/webp": {
    kind: "photo",
    extensions: [".webp"],
    // "RIFF" ???? "WEBP"
    magic: [[0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50]],
  },
  "image/heic": {
    kind: "photo",
    extensions: [".heic", ".heif"],
    // ???? "ftyp" then a brand: heic / heix / hevc / mif1 / msf1
    magic: [
      [null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69],
      [null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x6d, 0x69, 0x66, 0x31],
      [null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x6d, 0x73, 0x66, 0x31],
    ],
  },
  "image/avif": {
    kind: "photo",
    extensions: [".avif"],
    magic: [[null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x61, 0x76, 0x69]],
  },
  "image/gif": {
    kind: "photo",
    extensions: [".gif"],
    magic: [[0x47, 0x49, 0x46, 0x38]],
  },
  "video/mp4": {
    kind: "video",
    extensions: [".mp4", ".m4v"],
    magic: [[null, null, null, null, 0x66, 0x74, 0x79, 0x70]],
  },
  "video/quicktime": {
    kind: "video",
    extensions: [".mov"],
    magic: [[null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x71, 0x74]],
  },
  "video/webm": {
    kind: "video",
    extensions: [".webm"],
    magic: [[0x1a, 0x45, 0xdf, 0xa3]],
  },
};

/**
 * SVG is deliberately absent. It is an XML document that can contain scripts
 * and external references; treating it as an image is a stored-XSS vector.
 * TIFF and RAW are absent for now because they need a different decode path —
 * adding them is a worker change, not an architecture change.
 */
export const REJECTED_EXTENSIONS = [".svg", ".svgz", ".html", ".htm", ".xhtml", ".pdf"] as const;

/**
 * Original types that may be served for viewing rather than as a download.
 *
 * An original is the only object in the system that is *not* re-encoded, so it
 * is exactly the bytes somebody uploaded — which is why every original is
 * normally served `Content-Disposition: attachment`, so a file that slipped
 * through validation cannot be rendered as active content on the storage
 * origin.
 *
 * That default breaks the one thing a phone needs. iOS saves a picture through
 * long-press → "Save to Photos", which re-fetches the image's URL; handed an
 * attachment it treats the result as a download, fails, and reports a network
 * error. The full-resolution view therefore has to be servable inline.
 *
 * This list is what that exception is allowed to cover: raster formats with no
 * document semantics, each served with this exact `Content-Type` so nothing is
 * left to sniffing. Deliberately absent:
 *
 *   · **SVG** — an XML document with a scripting engine. Rejected at upload and
 *     never to be added here, whatever else changes.
 *   · **video** — playback never needed the exception, and a container is a far
 *     richer parser to hand a navigation to.
 *   · **GIF** — accepted for upload, but it is the one raster format whose
 *     decoders have the worst history, and nothing asks to view one full size.
 */
export const VIEWABLE_ORIGINAL_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/avif",
]);

export function mediaKindForMime(mime: string): FileKind | null {
  return ACCEPTED_MEDIA_TYPES[mime.toLowerCase()]?.kind ?? null;
}

/**
 * The type a file claims by its name, when the browser will not say.
 *
 * Phones are unreliable here. Picking a clip from an iOS photo library can hand
 * over a `File` whose `type` is the empty string, and some Android pickers do
 * the same for anything they do not recognise. Rejecting those means a person
 * watches a video refuse to upload with no explanation and no way to fix it,
 * for a file that is perfectly acceptable.
 *
 * The name is a hint, not evidence — the server still checks the magic bytes
 * before it stores anything, so a lie here buys nothing.
 */
export function mimeForFilename(filename: string): string | null {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return null;

  const extension = filename.slice(dot).toLowerCase();
  for (const [mime, spec] of Object.entries(ACCEPTED_MEDIA_TYPES)) {
    if (spec.extensions.includes(extension)) return mime;
  }
  return null;
}

export function isAcceptedMime(mime: string): boolean {
  return mime.toLowerCase() in ACCEPTED_MEDIA_TYPES;
}

/**
 * Verify the real file type from its leading bytes.
 *
 * The declared MIME and the filename extension are both attacker-controlled.
 * This is the only check that looks at what the bytes actually are, and the
 * worker runs it before handing anything to a decoder.
 */
export function matchesMagicBytes(mime: string, head: Uint8Array): boolean {
  const spec = ACCEPTED_MEDIA_TYPES[mime.toLowerCase()];
  if (!spec) return false;

  return spec.magic.some((signature) => {
    if (head.length < signature.length) return false;
    return signature.every((byte, index) => byte === null || head[index] === byte);
  });
}

/** Detect the type from bytes alone, ignoring anything the client claimed. */
export function sniffMime(head: Uint8Array): string | null {
  for (const mime of Object.keys(ACCEPTED_MEDIA_TYPES)) {
    if (matchesMagicBytes(mime, head)) return mime;
  }
  return null;
}

/** Number of leading bytes the worker needs to read to identify any type. */
export const MAGIC_BYTES_WINDOW = 16;

/* ──────────────────────────── The variant ladder ─────────────────────────── */

export interface ImageVariantSpec {
  variant: Extract<Variant, "thumb" | "preview" | "preview_hq">;
  /** Longest edge in pixels. Images smaller than this are never upscaled. */
  maxEdge: number;
  formats: readonly { format: ImageFormat; quality: number }[];
  /** Generated during `image.process`, or only when a client asks for it. */
  eager: boolean;
}

export const IMAGE_VARIANT_LADDER: readonly ImageVariantSpec[] = [
  {
    variant: "thumb",
    maxEdge: 480,
    formats: [
      { format: "avif", quality: 50 },
      { format: "webp", quality: 72 },
    ],
    eager: true,
  },
  {
    variant: "preview",
    maxEdge: 2048,
    formats: [
      { format: "avif", quality: 58 },
      { format: "webp", quality: 78 },
    ],
    eager: true,
  },
  {
    // Only produced when someone actually zooms past 100% on a high-DPI
    // display. Most photos never get one, which saves roughly 40% of
    // derivative storage compared with generating the full ladder eagerly.
    variant: "preview_hq",
    maxEdge: 4096,
    formats: [{ format: "webp", quality: 82 }],
    eager: false,
  },
];

export interface VideoVariantSpec {
  variant: Extract<Variant, "video_720" | "video_1080">;
  height: number;
  videoBitrateKbps: number;
  audioBitrateKbps: number;
  /** Skip this rung when the source is not at least this tall. */
  minSourceHeight: number;
}

export const VIDEO_VARIANT_LADDER: readonly VideoVariantSpec[] = [
  { variant: "video_720", height: 720, videoBitrateKbps: 2500, audioBitrateKbps: 128, minSourceHeight: 0 },
  { variant: "video_1080", height: 1080, videoBitrateKbps: 5000, audioBitrateKbps: 128, minSourceHeight: 900 },
];

export const POSTER_MAX_EDGE = 480;

export function extensionForFormat(format: ImageFormat): string {
  return format === "jpeg" ? "jpg" : format;
}

export function mimeForFormat(format: ImageFormat): string {
  return format === "jpeg" ? "image/jpeg" : `image/${format}`;
}

/**
 * Pick the best format the client can actually display.
 *
 * Server-side negotiation from the Accept header, rather than shipping every
 * format to every client and letting `<picture>` sort it out — that would
 * double the metadata payload of a 500-tile gallery response.
 */
export function negotiateImageFormat(
  accept: string | undefined,
  available: readonly ImageFormat[],
): ImageFormat {
  const header = (accept ?? "").toLowerCase();
  const preference: ImageFormat[] = [];
  if (header.includes("image/avif")) preference.push("avif");
  if (header.includes("image/webp")) preference.push("webp");
  preference.push("jpeg", "webp", "avif");

  for (const format of preference) {
    if (available.includes(format)) return format;
  }
  return available[0] ?? "jpeg";
}

/* ──────────────────────────────── Limits ─────────────────────────────────── */

export interface MediaLimits {
  maxPhotoBytes: number;
  maxVideoBytes: number;
  /** Defuses decompression bombs: 16 384 × 16 384 is far beyond any camera. */
  maxPixels: number;
  maxVideoDurationSeconds: number;
  maxExportItems: number;
  maxExportBytes: number;
}

export const DEFAULT_MEDIA_LIMITS: MediaLimits = {
  maxPhotoBytes: 200 * 1024 * 1024,
  maxVideoBytes: 5 * 1024 * 1024 * 1024,
  /*
   * The decode ceiling in pixels — which is really the memory ceiling.
   *
   * libvips works on uncompressed raster, so this costs `pixels × 4` bytes of
   * RAM per concurrent decode. It was 16384² = 268 M, i.e. ~1 GiB for a single
   * photo: enough for the OOM reaper to kill a 512 MB container mid-upload,
   * leaving the photo stuck on "Processing" with nothing in the logs to say why.
   *
   * 8192² = 67 M sits comfortably above anything a camera produces — a 48 MP
   * iPhone shot is 48 M — and costs ~268 MB per decode. Raise it only alongside
   * the container's memory, remembering that worker concurrency multiplies it.
   */
  maxPixels: 8192 * 8192,
  maxVideoDurationSeconds: 4 * 60 * 60,
  maxExportItems: 5000,
  maxExportBytes: 20 * 1024 * 1024 * 1024,
};

export function maxBytesForKind(kind: FileKind, limits: MediaLimits = DEFAULT_MEDIA_LIMITS): number {
  return kind === "video" ? limits.maxVideoBytes : limits.maxPhotoBytes;
}

/* ───────────────────────── Multipart upload sizing ───────────────────────── */

/**
 * S3 requires every part except the last to be ≥5 MiB and allows at most
 * 10 000 parts. 8 MiB keeps part counts low for typical phone photos while
 * still giving useful resume granularity on a flaky mobile connection; the
 * size grows for very large files so the part count stays bounded.
 */
export const MIN_PART_BYTES = 5 * 1024 * 1024;
export const DEFAULT_PART_BYTES = 8 * 1024 * 1024;
export const MAX_PARTS = 10_000;

export function planParts(totalBytes: number): { partSize: number; partCount: number } {
  let partSize = DEFAULT_PART_BYTES;
  while (Math.ceil(totalBytes / partSize) > MAX_PARTS) {
    partSize *= 2;
  }
  const partCount = Math.max(1, Math.ceil(totalBytes / partSize));
  return { partSize, partCount };
}

/** Files below one part are uploaded with a single PUT — no multipart overhead. */
export function isSinglePartUpload(totalBytes: number): boolean {
  return totalBytes <= MIN_PART_BYTES;
}
